import { useState } from 'react';
import { useController } from 'react-hook-form';
import { withMask } from 'use-mask-input';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  type RegisteredFieldProps,
  registerFieldComponent,
} from '@/lib/component-registry';

/**
 * Campo composto de endereço (CEP + rua/bairro/cidade/estado/número/complemento),
 * registrado no component-registry (`type: 'component', component: 'customer-address'`
 * na screen de clientes) porque o form genérico não suporta efeito colateral entre
 * campos — aqui, o CEP autopreenche rua/bairro/cidade/estado via ViaCEP.
 */
export function CustomerAddressFields({ control, disabled }: RegisteredFieldProps) {
  const zipCode = useController({ name: 'zipCode', control });
  const street = useController({ name: 'street', control });
  const number = useController({ name: 'number', control });
  const complement = useController({ name: 'complement', control });
  const neighborhood = useController({ name: 'neighborhood', control });
  const city = useController({ name: 'city', control });
  const state = useController({ name: 'state', control });
  const [loadingCep, setLoadingCep] = useState(false);

  const handleCepBlur = async () => {
    const digits = String(zipCode.field.value ?? '').replace(/\D/g, '');
    if (digits.length !== 8) return;

    setLoadingCep(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (!data.erro) {
        street.field.onChange(data.logradouro ?? '');
        neighborhood.field.onChange(data.bairro ?? '');
        city.field.onChange(data.localidade ?? '');
        state.field.onChange(data.uf ?? '');
      }
    } catch {
      // ViaCEP indisponível — segue com o preenchimento manual.
    } finally {
      setLoadingCep(false);
    }
  };

  return (
    <div className="col-span-12 grid grid-cols-12 gap-4">
      <Field className="col-span-12 md:col-span-3">
        <FieldLabel>CEP</FieldLabel>
        <Input
          {...zipCode.field}
          value={zipCode.field.value ?? ''}
          onBlur={() => {
            zipCode.field.onBlur();
            handleCepBlur();
          }}
          ref={withMask('99999-999', { removeMaskOnSubmit: true })}
          placeholder={loadingCep ? 'Buscando...' : undefined}
          disabled={disabled}
        />
      </Field>

      <Field className="col-span-12 md:col-span-6">
        <FieldLabel>Rua</FieldLabel>
        <Input value={street.field.value ?? ''} disabled />
      </Field>

      <Field className="col-span-6 md:col-span-3">
        <FieldLabel>Número</FieldLabel>
        <Input
          value={number.field.value ?? ''}
          onChange={(e) => number.field.onChange(e.target.value)}
          disabled={disabled}
        />
      </Field>

      <Field className="col-span-12 md:col-span-4">
        <FieldLabel>Complemento</FieldLabel>
        <Input
          value={complement.field.value ?? ''}
          onChange={(e) => complement.field.onChange(e.target.value)}
          disabled={disabled}
        />
      </Field>

      <Field className="col-span-12 md:col-span-4">
        <FieldLabel>Bairro</FieldLabel>
        <Input value={neighborhood.field.value ?? ''} disabled />
      </Field>

      <Field className="col-span-6 md:col-span-3">
        <FieldLabel>Cidade</FieldLabel>
        <Input value={city.field.value ?? ''} disabled />
      </Field>

      <Field className="col-span-6 md:col-span-1">
        <FieldLabel>UF</FieldLabel>
        <Input value={state.field.value ?? ''} maxLength={2} disabled />
      </Field>
    </div>
  );
}

registerFieldComponent('customer-address', CustomerAddressFields);
